import React, { useEffect, useState } from 'react';
import { Button, Form, Popover, PopoverBody, PopoverHeader, Row, Card as BSCard, Card, CardBody, CardHeader, Col } from 'reactstrap';
import C from '../C';
import Roles from '../base/Roles';
import LinkOut from '../base/components/LinkOut';
import { DefaultCopy, DefaultDelete, DefaultListItem } from '../base/components/ListLoad';
import Misc from '../base/components/Misc';
import { getNavContext, setNavContext } from '../base/components/NavBar';
import PropControl from '../base/components/PropControl';
import SavePublishDeleteEtc from '../base/components/SavePublishDeleteEtc';
import { setTaskTags } from '../base/components/TaskList';
import Advert, { KAdFormat } from '../base/data/Advert';
import KStatus from '../base/data/KStatus';
import { getDataItem, setWindowTitle, getDataList } from '../base/plumbing/Crud';
import DataStore from '../base/plumbing/DataStore';
import SearchQuery from '../base/searchquery';
import { assert } from '../base/utils/assert';
import { sum, encURI, space, noVal } from '../base/utils/miscutils';
import { ServingBadge } from './AdvertPage';
import { CreateAdThingButton } from './ListItems';
import List from '../base/data/List';
import {AdvancedPage, CauseControlsPage} from './DisplayAdvertStaffPages';
import {DetailsPage, DesignPage, PublishPage} from './DisplayAdvertClientPages';
import { STANDARD_SIZES, CREATIVE_SIZES } from './DisplayAdvertUtils';
import { dateStr, dateUTCfromString} from '../base/utils/date-utils';
import SubCard from '../base/components/SubCard';
import { isNull } from 'util';

/** DisplayAdvert concerns the creation & management of display ads.
 * A display ad is a fixed, pixel defined banner ad. We current support 4 sizes, Leaderboard, Billboard, MPU2 & a responsive format. 
 * Size defs found in DisplayAdvertUtils.jsx
 * 
 * While we mark the size of the ad & lock it to that size, all ads are actually just responsive ads designed to fill as much space as possible. 
 * When viewed on a website, these ads will be in an appropriately sized iframe and by filling it, achieve the desired look
 * 
 * The is built from two parts. The outer advert and creative.
 * The outer advert is our preset "this ad plants trees" text, background & is defines the size. This is our ad container.
 * The inner creative is another iframe that is contained within the ad & is either HTML or an image uploaded by a client. This is their ad.
 * 
 * Details for this inner ad are contained within a displayBase object attached onto the ad. Of note, this object has its own branding.
 * I've tried to keep our advert branding & creative branding distinct by just doing: 
 *   	- outer advert branding -> advert.branding
 * 		- inner creative branding -> advert.displayBase.branding
 * 
 * @returns 
 */
function DisplayAdvertPage() {
	const type = C.TYPES.Advert;
	setWindowTitle(type);

	const blocked = Misc.CheckAccess({ can: 'editAdvert' });
	if (blocked) return blocked;

	const path = DataStore.getValue(['location', 'path']);
	const advertId = path[1];

	setNavContext(C.TYPES.Advert, null);

	// No ID? Show the list.
	if (!advertId) {
		setTaskTags(C.TYPES.Advert);
		return <ListDisplayAdverts />;
	}

	const item = getDataItem({ type, id: advertId, status: KStatus.DRAFT }).value;
	// Advert specified, but not loaded yet.
	if (!item) {
		return <><h1>Advert: {advertId}</h1><Misc.Loading /></>;
	}
	const adpath = DataStore.getDataPath({ type: "Advert", status: KStatus.DRAFT, id: item.id });

	setWindowTitle(item);

	// HACK race condition -- somehow you can get fragments put into DataStore, which aren't full adverts?? Seen Oct 2019
	if (!Advert.isa(item)) {
		const oldItem = item;
		// const base = Object.assign(item);
		// base.id = advertId; // make sure we have an ID!
		// // make sure we have an advertiser!
		// item = new Advert(base);
		// DataStore.setData({type:C.TYPES.Advert, id:advertId, status:C.KStatus.DRAFT}, item, false);
		console.warn("AdvertPage", "item is NOT an advert?!", oldItem);
		return <><h1>Advert: {advertId}</h1><Misc.Loading /></>;
	}

	Advert.assIsa(item);
	setTaskTags(C.TYPES.Advert, advertId, item.vertiser);
	return <EditAdvertForm path={adpath}/>;
} // ./DisplayAdvertPage



function EditAdvertForm({ path }) {
	let advert = DataStore.getValue(path);
	const dPath = [...path, 'displayBase'];

	Advert.assIsa(advert);
	if (!advert.id) {
		// worrying
		console.warn("Advert without an ID?!", advert);
		advert.id = C.newId;
	}
	assert(advert.id, advert);
	Advert.assIsa(advert);
	// a new advert?
	if (!advert.vertiser) {
		if (advert.id === C.newId) {
			const tid = getNavContext(C.TYPES.Advertiser);
			const urlv = DataStore.getUrlValue(C.NAV.vertiser);
			if (urlv && tid) assert(urlv === tid, "Advertiser mismatch: nav-context: " + tid + " vs url-param: " + urlv);
			advert.vertiser = tid || urlv;
		} else {
			// NB: can happen temporarily if editing who owns this advert
			console.warn("Advert without a .vertiser?!", advert);
		}
	}

	// we got the ad, load extra data not stored directly on it (note: all currently unused intended to be used soon)
	// impact debits
	let impactDebitPV = getDataList({type: C.TYPES.ImpactDebit, status: KStatus.DRAFT, q:new SearchQuery(`campaign:${advert.campaign}`), save : true});
	let impactDebits = [];
	if(impactDebitPV.resolved) impactDebits = List.hits(impactDebitPV.value);

	// impressions
	const viewcount4campaign = Advert.viewcountByCampaign(advert) || 0;
	let impressions = sum(Object.values(viewcount4campaign));
	// end loading extra data

	advert.format = KAdFormat.display;
	let vertiser;
	// HACK fill in Advertiser's name
	if (!advert.vertiserName && advert.vertiser) {
		const pvAdvertiser = getDataItem({ status: KStatus.PUBLISHED, type: C.TYPES.Advertiser, id: advert.vertiser, swallow: true });
		if (pvAdvertiser.value) {
			advert.vertiserName = pvAdvertiser.value.name;
			vertiser = pvAdvertiser.value;
		}
	}

	// are you staff, if so do you want to see the client view? Otherwise, we presume you to be a client
	const isStaff = Roles.isTester() ? !DataStore.getUrlValue("isClient") : false;
	let advertLoaded = !!advert.displayBase;	
	
	// if a field is required, give user a warning in navbar below page it's found within.
	// Each page will be passed a list of its required fields in the structure:
	// [ [Warning-Message-#1, Path-To-Object-#1], [Warning-Message-#2, Path-To-Object-#2], ...]
	// each list describes the required fields on a distinct page -> 'required[PAGE_NAME]'
	let requiredDetails = [
		['Advert Name', [...path, 'name']]
	];
	if(advertLoaded && !advert.displayBase.isTemplate) requiredDetails.push(...[['Campaign', [...path, 'campaign']], ['Click Through URL', [...path, 'url']]]);

	let requiredDesign = [];
	// if ads aren't a template itself, it will need a template
	if(advertLoaded && !advert.displayBase.isTemplate) {
		requiredDesign.push(['template', [...dPath, 'templateId']]);
		// check what type of creative we're using and enforce its existence
		requiredDesign.push(advert.displayBase.contentType === "HTML" ? ['Creative HTML',[...dPath, 'creativeBranding', 'customHtml']] : ['Creative Image',[...dPath, 'creativeBranding', 'backgroundImage']] );	
	}

	// cause isn't shown unless the ad is a template so these values are only required in that case
	let requiredCause = [];
	if(advertLoaded && advert.displayBase.isTemplate) {
		let colorOrImgBG = advert.branding.backgroundImage;

		requiredCause.push(['Charity', [...path, 'charities', 'list']]);
		requiredCause.push(['Noun', [...dPath, 'noun']]);
		requiredCause.push(['Verb', [...dPath, 'verb']]);
		requiredCause.push(['Action', [...dPath, 'action']]);
		if(colorOrImgBG) {
			requiredCause.push(['Background Image', [...path, 'branding', 'backgroundImage']]);
		} else {
			requiredCause.push(['Background Image', [...path, 'branding', 'backgroundColor']]);
		}
	}

	// check to see we actually have all of these before we let user publish the ad
	let allRequired = [...requiredDetails, ...requiredDesign, ...requiredCause];
	// end required field checking

	let size = DataStore.getValue([...path, "displayBase", "displaySize"]);

	let isTemplateSet = advertLoaded && advert.displayBase.isTemplate !== undefined;
	let formatChosen = isTemplateSet && advert.displayBase.displaySize !== undefined;

	let pageContent;
	if(!advertLoaded) pageContent = <Misc.Loading />; // required to be loaded as part of page else the savePublishDelete is never loaded, so an ad is never created!
	else if(!isTemplateSet) pageContent = <TemplateDecisionPage path={path} />;
	else if(!formatChosen) pageContent = <FormatDecisionPage path={path} advert={advert}/>;
	else {
		pageContent = (
		<div className="flex-row justify-content-center align-items-start w-100">
			<div id="tadg-display-page" className="d-flex flex-column bg-light rounded">
				<VerticalNav advert={advert}>
					<DetailsPage advert={advert} path={path} isStaff={isStaff} size={size} title="Details" required={requiredDetails}/>
					{!advert.displayBase.isTemplate && <DesignPage advert={advert} path={path} isStaff={isStaff} size={size} title="Design" required={requiredDesign}/>}
					<PublishPage advert={advert} path={path} size={size} isStaff={isStaff} title="Publish" requiredToPublish={allRequired}/>
					{isStaff && advert.displayBase.isTemplate && <CauseControlsPage advert={advert} path={path} size={size} title="Format Controls" staffOnly={isStaff} required={requiredCause}/>}
					{isStaff && <AdvancedPage advert={advert} path={path} size={size} title="Advanced" staffOnly={isStaff} />}
				</VerticalNav>
			</div>
		</div>
		);
	}

	return (
		<>
			{pageContent}
			<SavePublishDeleteEtc type={C.TYPES.Advert} id={advert.id} autoSave hidden/>
		</>
	);
}


function TemplateDecisionPage({path}) {
	const [templateChosen, setTemplateChosen] = useState(null);
	
	let templateImgSrc = `/img/display_template.${templateChosen ? "gif" : "png"}`;
	let advertImgSrc = `/img/display_advert.${!templateChosen && templateChosen !== null ? "gif" : "png"}`;

	const setTemplateMode = (isTemplate) => {
		DataStore.setValue([...path, "displayBase", "isTemplate"], isTemplate);
	};

	return (
		<div id="template-decision-page" className="full-page-absolute w-100">
			<div className='row'>
				<div className={`col template-col-content w-50 ${templateChosen && "selected"} ${!templateChosen && templateChosen !== null && "not-selected"}`} id='left-template-decision'>
					<h1 className='white'>Template</h1>
					<p>Create an outer template for later ads to use. Each template belongs to a specific charity and describes the good they & our partner brands can do.</p>
					<img src={templateImgSrc} className='template-img' />
					<Button className="select-template-btn mt-5 btn-dark" onClick={() => setTemplateChosen(true)}>Make A Template</Button>
				</div>
				<div className={`col template-col-content w-50 ${!templateChosen && templateChosen !== null && "selected"} ${templateChosen && templateChosen !== null && "not-selected"}`} id='right-template-decision'>
					<h1>Advert</h1>
					<p>Create an advert using preexisting templates. For a given brand, select their chosen Impact Debit and provide the creative associated with it.</p>
					<img src={advertImgSrc} className='template-img' />
					<Button className="select-template-btn mt-5 btn-dark" onClick={() => setTemplateChosen(false)}>Make An Advert</Button>
				</div>
				{templateChosen !== null && <Button className='enter-decision btn-danger' onClick={() => setTemplateMode(templateChosen)}>Create {templateChosen ? "Template" : "Advert"}</Button>}
			</div>
			</div>
	);
}

function FormatDecisionPage({path, advert}) {
	/* return (
		<>
		<h1>Format picking time...</h1>
		<div className="d-flex flex-row justify-content-between align-items-center mb-3">
			{Object.keys(STANDARD_SIZES).map(sizeVal => <Button id={sizeVal} onClick={() => DataStore.setValue(displayPath.concat("displaySize"), sizeVal)}/>)}
		</div>
		</>
	); */

	const [sizeChosen, setSizeChosen] = useState(null);
	
	const setTemplateMode = () => {
		console.log(sizeChosen, "setting size to this!", path);
		DataStore.setValue([...path, "displayBase", "displaySize"], sizeChosen);
	};
	const [adDimensions, setAdDimensions] = useState([0,0]);

	const changeSize = (size) => {
		setSizeChosen(size);
		setAdDimensions(STANDARD_SIZES[size]);
	};

	let sizeDescs = {
		mpu: "this is a description about this ad size. It will give advert dimensions, creative dimensions, and a description of a use case (?) ",
		mpu2:  "this is a description about this ad size. It will give advert dimensions, creative dimensions, and a description of a use case (?) ",
		billboard: "this is a description about this ad size. It will give advert dimensions, creative dimensions, and a description of a use case (?) ",
		leaderboard:  "this is a description about this ad size. It will give advert dimensions, creative dimensions, and a description of a use case (?) ",
		responsive:  "this is a description about this ad size. It will give advert dimensions, creative dimensions, and a description of a use case (?) ",
	};

	let sizeImgs = {
		mpu: "/img/template-mpu.png",
		mpu2: "/img/template-mpu.png",
		billboard: "/img/template-billboard.png",
		leaderboard: "/img/template-leaderboard.png",
		responsive: "/img/template-responsive.png" 
	};

	let textElement = Object.keys(sizeDescs).map((size) => {
		return 
	});

	let imgElement = Object.keys(sizeImgs).map((size) => {
		return <img className={`size-preview ${size === sizeChosen && "selected"}`} alt={`Preview for display advert of size : ${size}`} id={size} />;
	});

	const templateStr = advert.displayBase.isTemplate ? "template" : "advert";
	return (
		<div id="template-picker-page" className={`full-page-absolute w-100 ${templateStr}`}>
			<h1 className={templateStr === "template" && "white"}>Format</h1>
			<p>For your new {templateStr}, please select a template. This defines the resulting advert will be displayed at, as well as the recommended size of its creative. This cannot be changed after this decision.</p>
			<div className="size-buttons">
				{Object.keys(STANDARD_SIZES).map(sizeVal => <Button id={sizeVal} className={`${sizeVal === sizeChosen && "selected"} size-button btn-dark`} onClick={() => changeSize(sizeVal)}>{sizeVal}</Button>)}
			</div>
			{sizeChosen !== null && <p className="size-description" id={sizeChosen}>{sizeDescs[sizeChosen]}</p>}
			{sizeChosen !== null && <img className="size-preview" src={sizeImgs[sizeChosen]} alt={`Preview for display advert of size : ${sizeChosen}`} id={sizeChosen} />}
			{sizeChosen !== null && <Button className="enter-decision btn-danger" onClick={() => setTemplateMode(sizeChosen)}>Create {sizeChosen} {templateStr}</Button>}
		</div>
	);
}

/**
 * Vertical Navbar, heavily built out of the WizardProgressWidget
 * 
 * Decision to use vertical navbar allowed us to have more screen space to work on which helps solve the issues with sizing caused by - 
 * billboard ads being massive
 * 
 * @param {*} widgetName, name for navbar - used only by DataStore data storing/fetching
 * @param {path} stagePath, not required, used to check where we are on path
 * @param {jsx children} children, stages
 * @param {advert}  
 * @returns 
 */
function VerticalNav({widgetName, stagePath, children, advert}) {
	if(!stagePath) stagePath = ['widget', widgetName || 'verticalNav', 'stage'];
	let stageNum = DataStore.getValue(stagePath);
	if(!stageNum) stageNum = 0;
	if(!children) return <div className="verticalNav" />;

	children = children.filter(x => !! x);
	// get stage info for the progress bar {title, color, ...props}
	let stages = children.map( (kid, i) => {
		let props = Object.assign({}, kid.props);
		if ( ! props.title) props.title = 'Step '+i;
		return props;
	});
	// so next can recognise the end
	const maxStage = stages.length - 1;
	// add overview stage info to the stages
	let kids = React.Children.map(children, (Kid, i) => {
		// active?
		if (i != stageNum) {
			return null;
		}
		// clone with stageNum/path/key
		return React.cloneElement(Kid, {stageNum, stagePath, stageKey:i, maxStage});
	});
	// filter null again (we should now only have the active stage)
	kids = kids.filter(x => !! x);
	let activeStage = kids[0];
	console.log(activeStage);
	return <div id="page-content">
		<div className="d-flex">
			<VerticalNavControls stages={stages} stagePath={stagePath} stageNum={stageNum} advert={advert}/>
			{kids}
		</div>
	</div>;
}

function VerticalNavControls({stages, stagePath, stageNum}) {
	if ( ! stageNum) stageNum = 0;

	return (<div className="display-navbar">
		<div className="sticky-nav">
		{stages.map((stage, i) => <VerticalNavStage title={stage.title} stageNum={stageNum} i={i} stagePath={stagePath} staffOnly={stage.staffOnly} advert={stage.advert} required={stage.required}/>)}
		</div>
	</div>);
}

function VerticalNavStage({i, title, stageNum, stagePath, staffOnly, advert, required = []}) {
	let active = i === stageNum ? "active" : "";	
	const changeStage = () => {
		DataStore.setValue(stagePath, i);
		window.scrollTo(0, 0);
	};

	let missingValues = required.reduce((totalMissing, curVal, j) => {
		// try/catch used here to avoid DataStore throwing an error while reading null objects
		try {
			let value = DataStore.getValue(curVal[1]);
			let missingValue = noVal(value) || value === "" || (value.length && value.length === 0);
			if (!missingValue) return totalMissing;
			return [...totalMissing, <button onClick={changeStage} className={`unfinished-warning ${totalMissing.length === 0 && 'first'} ${active}`}>{"Missing " + curVal[0]}</button>];
		} catch {
			return totalMissing;
		} 
	}, []);
	
	let glOnly = <p className="unfinished-warning gl-only">Good Loop Only</p>;

	return (
		<>
			<Button className={`display-nav-btn ${active}`} key={i} stageNum={stageNum} i={i} stagePath={stagePath} onClick={changeStage}>{title}</Button>
			{missingValues}
			{staffOnly && glOnly}
		</>
	);
}

function fetchDisplayAdverts({advertiser}) {
	let query = "format:display";

	let sizeFilter = DataStore.getValue(['widget', 'filterSize']) || undefined;
	let templateFilter = DataStore.getValue(['widget', 'filterTemplates']) || undefined;
	let nameFilter = DataStore.getValue(['widget', 'filterName']) || undefined;

	let q = new SearchQuery(query);
	if(advertiser) q = new SearchQuery(`${query} && vertiser:${advertiser.id}`);

	let pvAds = getDataList({ type: C.TYPES.Advert, status:KStatus.ALL_BAR_TRASH, q, save: true });
	if(!pvAds.value) return null;
	let ads = List.hits(pvAds.value);

	console.log("filtering...", sizeFilter, templateFilter, nameFilter);
	ads = ads.filter((ad) => {
		// filter size === ad size
		if(sizeFilter !== undefined && sizeFilter !== "all" && ad.displayBase.displaySize !== sizeFilter) return false;

		// is template filter 
		if(templateFilter !== undefined && templateFilter !== "all") {
			if(templateFilter === "templates") return ad.displayBase.isTemplate;
			return !ad.displayBase.isTemplate;
		}

		// name & id filter
		if(nameFilter !== "" && nameFilter !== undefined) {
			return ((ad.name !== undefined && ad.name.toLowerCase().includes(nameFilter.toLowerCase())) || ad.id.toLowerCase().includes(nameFilter.toLowerCase()));
		}
		return true;
	});

	
	return ads;
}

function ListDisplayAdverts({advertiser}) {	
	if (!DataStore.getUrlValue('q')) DataStore.setUrlValue('q', 'format:display');
	let ads = fetchDisplayAdverts({advertiser});

	return (<BSCard><CardHeader>Advertisers Adverts</CardHeader>
		<Row className='w-50 p-2 pl-5'>
			<Col><PropControl path={['widget']} type="text" prop="filterName" label="Filter"/></Col>
			<Col><PropControl path={['widget']} type="select" prop="filterTemplates" label="Templates" options={['all', 'templates', 'adverts']}/></Col>
			<Col><PropControl path={['widget']} type="select" prop="filterSize" label="Format" default="all" options={['all', 'mpu', 'mpu2', 'billboard', 'leaderboard', 'responsive']}/></Col>
			<Col className='w-20 align-self-center'><CreateAdThingButton type={C.TYPES.Advert} navpage="displayad" createWith={[C.TYPES.Advertiser]}>Create a Display Advert</CreateAdThingButton></Col>
		</Row>
		<CardBody>	
			<div className="gridbox gridbox-md-4">
				{ads !== null ? ads.sort((a, b) => {
					if(!a.created && !b.created) return 0; // can happen when comparing two unpublished ads
					if(!a.created) return 1;
					if(!b.created) return -1;
					return dateUTCfromString(b.created) - dateUTCfromString(a.created); 
				}).map(ad => {
					return (
					<SubCard key={ad.id} className={space("m-2",ad._hidden && "disabled")}>
						<div className='d-flex flex-row justify-content-between align-items-end'>
							<a href={'/#displayad/'+encURI(ad.id)}>{ad.name || "unnamed"}</a>
						</div>
						<small>ID: {ad.id}</small>
						{ad.displayBase.isTemplate && <small className='font-weight-bold'>Template Advert</small>}
						{Object.keys(ad.displayBase).includes("isTemplate") === false && <small className='text-danger'>Template or Advert not set</small>}
						{Object.keys(ad.displayBase).includes("isTemplate") && ad.displayBase.displaySize ? <small>{ad.displayBase.displaySize}</small> : <small className='text-danger'>Size not set</small>}
						{ad.created ? <small>Created: {dateStr(dateUTCfromString(ad.created))}</small> : <small>Created: not published yet</small>}
						{ad.lastModified && <small>Last modified: {dateStr(dateUTCfromString(ad.lastModified))}</small>}
						{ad.serving && <small className='text-success'>Ad currently serving</small>}
						{ad.hasServed && <small className='text-success'>Ad previously served</small>}
						{ad.branding && ad.branding.logo && <img src={ad.branding.logo} className='position-absolute' style={{bottom: 0, right:0, width:"15%"}}/>}
						<div className='align-self-start' style={{marginLeft:"-2%"}}>
							<DefaultCopy type={C.TYPES.Advert} id={ad.id} item={ad}/>
							<DefaultDelete type={C.TYPES.Advert} id={ad.id} />
						</div>
					</SubCard>
					);
				}
				) : <Misc.Loading />}
			</div>
		</CardBody>
	</BSCard>);
}

function DisplayAdFilters({query}) {

	//if(filterName) query = `${query} && displayBase.isTemplate:true`;
}


export default DisplayAdvertPage;
